import {isPlatformBrowser} from '@angular/common';
import {DestroyRef, Injectable, NgZone, PLATFORM_ID, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, ActivatedRouteSnapshot, NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router, RoutesRecognized} from '@angular/router';
import {Capacitor} from '@capacitor/core';
import {ScreenLoadTransport, ScreenLoadOutcome} from './screen-load.transport';
import {SCREEN_KEYS} from './screen-keys';

interface Opening {
  id: number;
  started: number;
  key?: string;
  ended: boolean;
  painting: boolean;
  revision: number;
}

export function screenKey(snapshot: ActivatedRouteSnapshot): string | undefined {
  const data = snapshot.data;
  const value = data['screenKey'] ?? data['screenKeys']?.[snapshot.paramMap.get(data['screenKeyParam']) ?? ''] ?? data['screenKeyFallback'];
  return typeof value === 'string' && SCREEN_KEYS.includes(value) ? value : undefined;
}

@Injectable()
export class ScreenLoadService {
  private readonly router = inject(Router);
  private readonly transport = inject(ScreenLoadTransport);
  private readonly zone = inject(NgZone);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private opening?: Opening;
  private first = true;

  constructor() {
    if (!this.browser) return;
    this.router.events.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(event => {
      if (event instanceof NavigationStart) this.start(event.id);
      if (event instanceof RoutesRecognized) this.recognize(event.id, event.state.root);
      if (event instanceof NavigationEnd && this.opening?.id === event.id) this.opening.ended = true;
      if (event instanceof NavigationError) this.finishNavigation(event.id, 'Error');
      if (event instanceof NavigationCancel || event instanceof NavigationSkipped) this.finishNavigation(event.id, 'Canceled');
    });
  }

  // Called by the owning page after Angular has committed its content to the DOM.
  rendered(route: ActivatedRoute, loading: boolean, failed: boolean, visible: () => boolean = () => true): void {
    const opening = this.opening;
    if (!opening?.ended || !this.isActive(route) || !visible()) return;
    if (loading) {
      opening.revision++;
      opening.painting = false;
      return;
    }
    if (failed) { this.finish(opening, 'Error'); return; }
    if (opening.painting) return;
    opening.painting = true;
    const revision = opening.revision;
    this.zone.runOutsideAngular(() => requestAnimationFrame(() => requestAnimationFrame(() => {
      if (this.opening !== opening || revision !== opening.revision || !this.isActive(route)) return;
      opening.painting = false;
      if (visible()) this.finish(opening, 'Ready');
    })));
  }

  private start(id: number): void {
    if (this.opening) this.finish(this.opening, 'Canceled');
    this.opening = {id, started: this.first ? 0 : performance.now(), ended: false, painting: false, revision: 0};
    this.first = false;
  }

  private recognize(id: number, root: ActivatedRouteSnapshot): void {
    if (this.opening?.id !== id) return;
    let leaf = root;
    while (leaf.firstChild) leaf = leaf.firstChild;
    this.opening.key = screenKey(leaf);
  }

  private isActive(route: ActivatedRoute): boolean {
    let leaf = this.router.routerState.root;
    while (leaf.firstChild) leaf = leaf.firstChild;
    return leaf === route || (!!leaf.snapshot && leaf.snapshot === route.snapshot);
  }

  private finishNavigation(id: number, outcome: ScreenLoadOutcome): void {
    if (this.opening?.id === id) this.finish(this.opening, outcome);
  }

  private finish(opening: Opening, outcome: ScreenLoadOutcome): void {
    if (this.opening !== opening) return;
    this.opening = undefined;
    if (!opening.key) return;
    this.transport.send({screenKey: opening.key, elapsedMs: Math.min(3600000, Math.max(0, performance.now() - opening.started)),
      outcome, platform: Capacitor.getPlatform() === 'android' ? 'Android' : 'Web'});
  }
}
